export interface AiFactorScore {
  readonly key: AiFactorKey;
  readonly score: number;
  readonly delta: number | null;
}

export interface AiFactorScores {
  readonly postureIndex: number;
  // Keys of the AI-source connectors behind these scores, when the API says.
  readonly sources?: readonly string[];
  readonly factors: readonly AiFactorScore[];
}

export const AI_FACTOR_SOURCES = [
  { key: 'LANGFUSE', label: 'Langfuse' },
  { key: 'GEMINI', label: 'Gemini' },
  { key: 'CLAUDE', label: 'Claude' },
] as const;

// Scores predate the sources field, and were always Langfuse.
const DEFAULT_AI_FACTOR_SOURCE_KEYS: readonly string[] = ['LANGFUSE'];

export const getAiFactorSources = (keys?: readonly string[]) => {
  const wanted = keys?.length ? keys : DEFAULT_AI_FACTOR_SOURCE_KEYS;
  return AI_FACTOR_SOURCES.filter((source) => wanted.includes(source.key));
};

// Reference marks on the track: the plotted score is never clamped to them.
export const AI_FACTOR_FLOOR = 20;
export const AI_FACTOR_CEILING = 80;

const STRONG_SCORE = 80;
const MODERATE_SCORE = 50;

// statusWords are ordered strong → moderate → elevated, matching the scores above.
export const AI_FACTORS = [
  {
    key: 'exposure',
    label: 'AI Exposure',
    question: 'How bad could it be if this AI fails?',
    explanation:
      'Blast radius — what it is allowed to do, what it can touch, whether a human approves consequential actions.',
    statusWords: ['Limited exposure', 'Moderate exposure', 'Elevated exposure'],
    color: 'blue.6',
  },
  {
    key: 'vulnerability',
    label: 'AI Vulnerability',
    question: 'How often does it actually go wrong?',
    explanation:
      'Leaks, wrong answers, being manipulated, operational failures found in its own traffic.',
    statusWords: ['Strong', 'Moderate', 'Elevated'],
    color: 'violet.6',
  },
  {
    key: 'assurance',
    label: 'AI Assurance',
    question: 'How well is it governed?',
    explanation:
      'Controls we can see working — confirming before acting, refusing bad requests, monitoring, versioning.',
    statusWords: ['Strong', 'Moderate', 'Weak'],
    color: 'teal.7',
  },
] as const;

export type AiFactorDescriptor = (typeof AI_FACTORS)[number];
export type AiFactorKey = AiFactorDescriptor['key'];
export const AI_FACTOR_KEYS = AI_FACTORS.map((factor) => factor.key);

export const getFactorStatus = (
  score: number,
  statusWords: AiFactorDescriptor['statusWords']
) => {
  if (score >= STRONG_SCORE) {
    return statusWords[0];
  }
  if (score >= MODERATE_SCORE) {
    return statusWords[1];
  }
  return statusWords[2];
};
